/*
ScoreRiskModal — capture one scoring event for a risk.

Sliders (1-5) for Likelihood, Severity, Control Existence, Control
Effectiveness. Live preview of inherent (L*S) and residual (server formula
mirrored client-side for display only — server is authoritative). Optional
assessment-program picker (blank = adhoc) and notes.

Also surfaces the Vulnerabilities for this risk (analogue of control-test
deficiencies). User can Raise Vulnerability from the bottom-left chip; list
of existing vulnerabilities for this risk renders proximate to the button.
*/

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, AlertTriangle, Plus } from 'lucide-react';
import riskScoresService, { AssessmentInstanceLite, RiskScoreInput } from '../../services/riskScoresService';
import {
    riskVulnerabilityService,
    RiskVulnerability,
    VulnerabilitySeverity,
    VULNERABILITY_SEVERITIES,
} from '../../services/riskVulnerabilityService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import ScoreSlider from '../common/ScoreSlider';
import extractApiError from '../../utils/apiError';
import { useFieldValidation } from '../../validation/useFieldValidation';
import Toast, { type ToastMessage } from '../common/Toast';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    riskId: number;
    riskName?: string;
    onSaved: () => void;
}

const SCORE_COLOR = (s: number) => {
    if (s >= 15) return 'text-red-600';
    if (s >= 8) return 'text-orange-600';
    return 'text-emerald-600';
};

// merged: incoming + existing
const Slider = ({ label, value, onChange, testId }: { label: string; value: number; onChange: (v: number) => void; testId?: string }) => (
    <div>
        <div className="flex justify-between items-center mb-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</label>
            <span className="text-sm font-bold text-slate-700 w-6 text-center">{value}</span>
        </div>
        <input
            type="range"
            min={1}
            max={5}
            step={1}
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            className="w-full accent-blue-600"
            data-testid={testId}
        />
        <div className="flex justify-between text-[9px] text-slate-400 mt-0.5 px-0.5">
            <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
        </div>
    </div>
);

const severityClass = (s?: string | null) => {
    switch (s) {
        case 'Critical':       return 'status-badge xs danger';
        case 'High':           return 'status-badge xs warning';
        case 'Medium':         return 'status-badge xs warning';
        case 'Low':            return 'status-badge xs info';
        case 'Informational':  return 'status-badge xs neutral';
        default:               return 'status-badge xs neutral';
    }
};

const vulnStatusClass = (s?: string | null) => {
    switch (s) {
        case 'Open':         return 'status-badge xs danger';
        case 'In-Progress':  return 'status-badge xs warning';
        case 'Resolved':     return 'status-badge xs success';
        case 'Closed':       return 'status-badge xs neutral';
        default:             return 'status-badge xs neutral';
    }
};

const ScoreRiskModal: React.FC<Props> = ({ isOpen, onClose, riskId, riskName, onSaved }) => {
    const { formatDate } = useEntitySettings();
    const [likelihood, setLikelihood] = useState(3);
    const [severity, setSeverity] = useState(3);
    const [existence, setExistence] = useState(3);
    const [effectiveness, setEffectiveness] = useState(3);
    const [dateOfTest, setDateOfTest] = useState<string>(() => new Date().toISOString().slice(0, 10));
    const [programs, setPrograms] = useState<AssessmentInstanceLite[]>([]);
    const [programId, setProgramId] = useState<number | ''>('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Vulnerabilities raised against this risk (entity-wide, filtered to risk_id).
    const [vulnerabilities, setVulnerabilities] = useState<RiskVulnerability[]>([]);
    const [showRaiseDialog, setShowRaiseDialog] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        // reset form
        setLikelihood(3); setSeverity(3); setExistence(3); setEffectiveness(3);
        setDateOfTest(new Date().toISOString().slice(0, 10));
        setProgramId(''); setNotes(''); setToast(null);
        riskScoresService.listActivePrograms().then(setPrograms).catch(() => setPrograms([]));
        // Load existing vulnerabilities for this risk (any source).
        riskVulnerabilityService.listForRisk(riskId).then(setVulnerabilities).catch(() => setVulnerabilities([]));
    }, [isOpen, riskId]);

    const inherent = useMemo(() => likelihood * severity, [likelihood, severity]);
    const residual = useMemo(() => {
        const gap = (10 - existence - effectiveness) / 10;
        return Math.max(1, Math.min(25, Math.round(inherent * gap)));
    }, [inherent, existence, effectiveness]);
    // Display-only derivations that make the residual formula legible:
    //   control effectiveness % = (existence + effectiveness) / 10
    //   control deficiency %    = 100 − effectiveness %   (the gap that carries through)
    //   residual                = round(inherent × deficiency %)
    const effectivenessPct = useMemo(
        () => Math.round(((existence + effectiveness) / 10) * 100),
        [existence, effectiveness],
    );
    const deficiencyPct = useMemo(() => 100 - effectivenessPct, [effectivenessPct]);

    if (!isOpen) return null;

    const handleSave = async () => {
        setSubmitting(true);
        setToast(null);
        try {
            const input: RiskScoreInput = {
                likelihood, severity,
                control_existence: existence,
                control_effectiveness: effectiveness,
                date_of_test: dateOfTest || undefined,
                assessment_instance_id: programId === '' ? null : programId,
                notes: notes || undefined,
            };
            await riskScoresService.create(riskId, input);
            onSaved();
            onClose();
        } catch (e) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to record score') });
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
                <div className="p-5 border-b border-slate-100 flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Record Risk Score</h2>
                        {riskName && <p className="text-xs text-slate-500 mt-0.5">{riskName}</p>}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="BTN_risk_score_close"><X size={22} /></button>
                </div>

                <div className="p-5 space-y-5 overflow-y-auto">
                    <div className="border-2 border-gray-400 rounded-lg shadow-sm bg-white">
                        <div className="grid grid-cols-2 gap-6 p-4">
                            <div className="space-y-4">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Inherent Risk</h3>
                                <ScoreSlider label="Likelihood" value={likelihood} onCommit={setLikelihood} widthClass="w-full" invert />
                                <ScoreSlider label="Severity"   value={severity}   onCommit={setSeverity}   widthClass="w-full" invert />
                                <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                                    <span className="text-xs text-slate-500 uppercase">Inherent (L × S)</span>
                                    <span className={`text-2xl font-black ${SCORE_COLOR(inherent)}`}>{inherent}</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Control Assessment</h3>
                                <ScoreSlider label="Existence"     value={existence}     onCommit={setExistence}     widthClass="w-full" />
                                <ScoreSlider label="Effectiveness" value={effectiveness} onCommit={setEffectiveness} widthClass="w-full" />
                                <div className="pt-2 border-t border-slate-200 space-y-1.5">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-500 uppercase">Control Effectiveness</span>
                                        <span className="text-sm font-bold text-emerald-600">{effectivenessPct}%</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-500 uppercase">Control Deficiency</span>
                                        <span className="text-sm font-bold text-red-600">{deficiencyPct}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
                            <div className="flex items-center justify-center gap-x-2 gap-y-1 flex-wrap text-sm font-semibold text-slate-500">
                                <span className="uppercase text-xs tracking-wide">Residual</span>
                                <span className="text-slate-400">=</span>
                                <span className="inline-flex items-center gap-1">
                                    Inherent
                                    <span className={`font-black ${SCORE_COLOR(inherent)}`}>{inherent}</span>
                                </span>
                                <span className="text-slate-400">×</span>
                                <span className="inline-flex items-center gap-1">
                                    Deficiency
                                    <span className="font-black text-red-600">{deficiencyPct}%</span>
                                </span>
                                <span className="text-slate-400">=</span>
                                <span className={`text-2xl font-black ${SCORE_COLOR(residual)}`}>{residual}</span>
                            </div>
                            <p className="text-[10px] text-slate-400 text-center mt-1">
                                Controls offset inherent risk by their effectiveness ({effectivenessPct}%); the remaining {deficiencyPct}% deficiency carries through as residual.
                            </p>
                        </div>

                        {/* Vulnerabilities strip — bottom-left button + proximate list */}
                        <div className="px-3 py-2 border-t bg-rose-50/40 flex items-start gap-3">
                            <button
                                type="button"
                                onClick={() => setShowRaiseDialog(true)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 shrink-0"
                            >
                                <AlertTriangle size={12} /> Raise Vulnerability
                            </button>
                            {vulnerabilities.length > 0 && (
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-rose-700 mb-1">
                                        Vulnerabilities ({vulnerabilities.length})
                                    </div>
                                    <ul className="flex flex-col gap-1">
                                        {vulnerabilities.map(v => (
                                            <li key={v.id} className="flex items-start gap-2 text-xs">
                                                <span className={severityClass(v.detected_severity)}>
                                                    {v.detected_severity}
                                                </span>
                                                <span className={vulnStatusClass(v.status)}>
                                                    {v.status}
                                                </span>
                                                <span className="text-gray-700 flex-1 whitespace-pre-wrap">{v.description}</span>
                                                <span className="text-gray-400">{formatDate(v.detected_on)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <FormControl.DatePicker
                                label="Date of Test"
                                value={dateOfTest}
                                onChange={(e) => setDateOfTest(e.target.value)}
                                data-testid="INP_risk_score_date"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Program (optional)</label>
                            <FormControl.SearchSelect
                                noMargin
                                value={programId === '' ? null : programId}
                                onChange={(v) => setProgramId(v == null ? '' : (v as number))}
                                options={programs.map(p => ({
                                    value: p.id,
                                    label: `${p.program_code}${p.origin ? ` · ${p.origin}` : ''}`,
                                }))}
                                placeholder="— Adhoc (no program) —"
                                clearLabel="— Adhoc (no program) —"
                                clearable
                                data-testid="SEL_risk_score_program"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="What changed? Why this score?"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm h-20"
                            data-testid="INP_risk_score_notes"
                        />
                    </div>
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-3">
                    <Button
                        variant="fill"
                        icon={Save}
                        onClick={handleSave}
                        disabled={submitting}
                        data-testid="BTN_risk_score_submit"
                    >
                        {submitting ? 'Saving…' : 'Record Score'}
                    </Button>
                </div>
            </div>

            {showRaiseDialog && (
                <RaiseVulnerabilityDialog
                    riskId={riskId}
                    sourceRefId={programId === '' ? null : (programId as number)}
                    onClose={() => setShowRaiseDialog(false)}
                    onCreated={(v) => {
                        setVulnerabilities(prev => [v, ...prev]);
                        setShowRaiseDialog(false);
                    }}
                />
            )}
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>,
        document.body
    );
};

interface RaiseVulnerabilityDialogProps {
    riskId: number;
    sourceRefId: number | null;
    onClose: () => void;
    onCreated: (v: RiskVulnerability) => void;
}

const RaiseVulnerabilityDialog: React.FC<RaiseVulnerabilityDialogProps> = ({ riskId, sourceRefId, onClose, onCreated }) => {
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState<VulnerabilitySeverity>('Medium');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const { errors, validateField, clearError, validateAll, reset } = useFieldValidation();

    useEffect(() => {
        setDescription('');
        setSeverity('Medium');
        setErr(null);
        reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskId]);

    const submit = async () => {
        const valid = validateAll([{ name: 'description', code: 110, value: description, required: true }]);
        if (!valid) return;
        if (!description.trim()) { setErr('Description is required'); return; }
        setSaving(true);
        try {
            const created = await riskVulnerabilityService.create(riskId, {
                description: description.trim(),
                source: 'assessment',
                source_ref_id: sourceRefId,
                detected_severity: severity,
            });
            onCreated(created);
        } catch (e: any) {
            setErr(extractApiError(e, 'Failed to raise vulnerability'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-sm font-bold text-slate-800">Raise Vulnerability</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                </div>
                <div className="p-4 flex flex-col gap-3">
                    <label className="text-xs font-medium text-gray-600">Severity<span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
                    <select
                        value={severity}
                        onChange={e => setSeverity(e.target.value as VulnerabilitySeverity)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                        data-testid="SEL_vuln_severity"
                    >
                        {VULNERABILITY_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label className="text-xs font-medium text-gray-600">Description<span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
                    <textarea
                        rows={5}
                        value={description}
                        onChange={e => { setDescription(e.target.value); clearError('description'); }}
                        onBlur={e => validateField('description', 110, e.target.value, { required: true })}
                        placeholder="Describe the vulnerability noticed during this assessment…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        data-testid="INP_vuln_description"
                    />
                    {errors['description'] && <div className="text-xs text-red-600">{errors['description']}</div>}
                    {err && <div className="text-xs text-red-600">{err}</div>}
                </div>
                <div className="p-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} disabled={saving} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Cancel</button>
                    <button
                        onClick={submit}
                        disabled={saving || !description.trim()}
                        className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                        <Plus size={16} /> {saving ? 'Saving…' : 'Raise Vulnerability'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScoreRiskModal;
